import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import "./CompactMultiSelectDropdown.scss";
import arrowIcon from "assets/icons/arrow-down.svg";
import SearchInput from "components/inputs/SearchInput/SearchInput";
import Loader from "components/global/Loader";
import { useTranslation } from "react-i18next";
import useFixedDropdownPosition from "../useFixedDropdownPosition";

export interface CompactSelectItem {
  value: number | string;
  label: string;
}

interface Props {
  label: string;
  items: CompactSelectItem[];
  selectedValues: (number | string)[];
  onChange: (values: (number | string)[]) => void;
  onSearch?: (value: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  useFixedMenu?: boolean;
  isTop?: boolean;
  isBot?: boolean;
  disabled?: boolean;
  compact?: boolean;
  displayMode?: "count" | "chips";
}

const CompactMultiSelectDropdown: React.FC<Props> = ({
  label,
  items,
  selectedValues,
  onChange,
  onSearch,
  onLoadMore,
  hasMore = false,
  loading = false,
  useFixedMenu = false,
  isTop = false,
  isBot = false,
  disabled = false,
  compact = false,
  displayMode = "count",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [shouldOpenUp, setShouldOpenUp] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const loadMoreTriggeredRef = useRef(false);
  const [search, setSearch] = useState("");
  const menuHeight = compact ? Math.min(items.length * 34 + 14, 230) : 230;
  const fixedMenuStyle = useFixedDropdownPosition(
    containerRef,
    isOpen,
    useFixedMenu,
    menuHeight,
  );

  const updateDropdownDirection = () => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const boundary =
      container.closest(".main-page-widget--box") ?? document.documentElement;
    const containerRect = container.getBoundingClientRect();
    const boundaryRect = boundary.getBoundingClientRect();
    const dropdownHeight = menuHeight;
    const spaceBelow = boundaryRect.bottom - containerRect.bottom;
    const spaceAbove = containerRect.top - boundaryRect.top;

    setShouldOpenUp(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
  };

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
        if (!onSearch) {
          setSearch("");
        }
        loadMoreTriggeredRef.current = false;
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const toggleOpen = () => {
    if (disabled) return;
    if (isOpen) {
      if (!onSearch) setSearch("");
      loadMoreTriggeredRef.current = false;
    } else {
      loadMoreTriggeredRef.current = false;
      updateDropdownDirection();
    }
    setIsOpen(!isOpen);
  };

  const handleToggle = (value: number | string) => {
    const updatedValues = selectedValues.includes(value)
      ? selectedValues.filter((item) => item !== value)
      : [...selectedValues, value];

    onChange(updatedValues);
  };

  const filteredItems = onSearch
    ? items
    : items.filter((item) =>
        item.label.toLowerCase().includes(search.toLowerCase()),
      );

  const handleSearch = (value: string) => {
    loadMoreTriggeredRef.current = false;
    setSearch(value);
    onSearch?.(value);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 40;

    if (!isNearBottom) {
      loadMoreTriggeredRef.current = false;
      return;
    }

    if (hasMore && !loading && !loadMoreTriggeredRef.current) {
      loadMoreTriggeredRef.current = true;
      onLoadMore?.();
    }
  };

  const dropdownContent = (
    <div
      ref={menuRef}
      aria-hidden={compact && !isOpen ? true : undefined}
      style={fixedMenuStyle}
      className={`
        compact-multi-select-dropdown__content
        ${isOpen ? "compact-multi-select-dropdown__content--opened" : ""}
        ${useFixedMenu ? "compact-multi-select-dropdown__content--portal" : ""}
        ${compact ? "compact-multi-select-dropdown__content--compact" : ""}
      `}
    >
      {!compact && (
        <SearchInput
          placeholder="Search..."
          value={search}
          onChange={handleSearch}
          isSmall
        />
      )}

      <div
        className="compact-multi-select-dropdown__list"
        onScroll={handleScroll}
        role={compact ? "listbox" : undefined}
        aria-multiselectable={compact || undefined}
      >
        {filteredItems.map((item) => {
          const isSelected = selectedValues.includes(item.value);

          return (
            <div
              key={item.value}
              className={`
                compact-multi-select-dropdown__item
                ${
                  isSelected
                    ? "compact-multi-select-dropdown__item--selected"
                    : ""
                }
              `}
              role={compact ? "option" : undefined}
              aria-selected={compact ? isSelected : undefined}
              tabIndex={compact && isOpen ? 0 : undefined}
              onKeyDown={
                compact
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleToggle(item.value);
                      }
                      if (event.key === "Escape") setIsOpen(false);
                    }
                  : undefined
              }
              onClick={() => {
                if (!disabled) handleToggle(item.value);
              }}
            >
              <span
                className="compact-multi-select-dropdown__item-text"
                title={item.label}
              >
                {item.label}
              </span>
            </div>
          );
        })}
        {!loading && filteredItems.length === 0 && (
          <div className="compact-multi-select-dropdown__empty">
            {t("nothingFound")}
          </div>
        )}
        {loading && (
          <div className="compact-multi-select-dropdown__loader">
            <Loader size={10} />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={`compact-multi-select-dropdown ${
        (isTop || shouldOpenUp) && !isBot
          ? "compact-multi-select-dropdown--open-up"
          : ""
      } ${disabled ? "compact-multi-select-dropdown--disabled" : ""} ${compact ? "compact-multi-select-dropdown--compact" : ""}`}
      aria-disabled={disabled}
    >
      {displayMode === "chips" ? (
        <div className="compact-multi-select-dropdown__header">
          {selectedValues.length > 0 && (
            <div className="compact-multi-select-dropdown__chips">
              {selectedValues.map((value) => {
                const name =
                  items.find((item) => item.value === value)?.label ??
                  String(value);
                return (
                  <span
                    className="compact-multi-select-dropdown__chip"
                    key={value}
                  >
                    <span title={name}>{name}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${name}`}
                      disabled={disabled}
                      onClick={() => handleToggle(value)}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <button
            type="button"
            className="compact-multi-select-dropdown__toggle"
            aria-label={label}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            disabled={disabled}
            onClick={toggleOpen}
            onKeyDown={(event) => {
              if (event.key === "Escape") setIsOpen(false);
            }}
          >
            {selectedValues.length === 0 && <span>{label}</span>}
            <img
              className={`compact-multi-select-dropdown__arrow ${isOpen ? "compact-multi-select-dropdown__arrow--opened" : ""}`}
              src={arrowIcon}
              alt=""
            />
          </button>
        </div>
      ) : (
        <div
          className="compact-multi-select-dropdown__header"
          onClick={toggleOpen}
        >
          <span>{selectedValues.length} selected</span>
          <img
            className={`compact-multi-select-dropdown__arrow ${isOpen ? "compact-multi-select-dropdown__arrow--opened" : ""}`}
            src={arrowIcon}
            alt="arrow"
          />
        </div>
      )}

      {useFixedMenu && isOpen
        ? createPortal(dropdownContent, document.body)
        : dropdownContent}
    </div>
  );
};

export default CompactMultiSelectDropdown;
